import { useState } from "react";
import {
	createTripWorkspace,
	decodeShare,
	encodeShare,
	parsePastedTrip,
	selectShare,
	settleExpenses,
	type TripWorkspace,
} from "@japangolmok/core";
import { packingSuggestions } from "@japangolmok/content";
import { Layout, usePageMeta } from "../ui/Shell";
import { channelShareLink } from "../trips/ShareSheet";
export const guideTitles = {
	"share-trip": "일본 여행 일정 일행과 공유하기",
	"trip-split": "여행 정산 계산기",
	packing: "일본여행 준비물 체크리스트",
	"move-trip": "다른 여행 앱 일정 옮겨오기",
} as const;
export function GuidePage({ slug }: { slug: keyof typeof guideTitles }) {
	const [text, setText] = useState(""),
		[moveStart, setMoveStart] = useState(""),
		[month, setMonth] = useState(10),
		[packingSelected, setPackingSelected] = useState<string[]>([]),
		[companion, setCompanion] = useState("other"),
		[days, setDays] = useState(4),
		[people, setPeople] = useState(3),
		[amount, setAmount] = useState(12000),
		[sample, setSample] = useState(""),
		[error, setError] = useState("");
	usePageMeta(
		`${guideTitles[slug]} — 일본골목`,
		"설치·가입 없이 공유와 정산, 준비물과 일정 옮겨오기를 바로 해 보세요.",
	);
	const rows = moveStart ? parsePastedTrip(text, moveStart) : [];
	const t = createTripWorkspace(
		{
			title: "여행 정산",
			origin: "PUS",
			destination: "FUK",
			destinationCity: "후쿠오카",
			startDate: "2026-10-26",
			endDate: "2026-10-28",
			partySize: Number.isInteger(people)
				? Math.min(12, Math.max(1, people))
				: 1,
			styles: [],
			dailyBudgetJpy: 15000,
		},
		"2026-10-19T00:00:00Z",
		"sample",
	);
	t.expenses = [
		{
			id: "expense",
			title: "공동 지출",
			category: "other",
			occurredOn: t.startDate,
			amountJpy: Math.max(1, Math.round(amount) || 1),
			paidBy: "me",
		},
	];
	return (
		<Layout>
			<article className="section guide-page">
				<h1>{guideTitles[slug]}</h1>
				{slug === "share-trip" && (
					<>
						<p>
							일정을 만든 사람이 내용을 확인하고 링크를 보내면, 받은 일행은 가입
							없이 보고 의견을 답장할 수 있어요.
						</p>
						<ol>
							<li>여행의 장소와 날짜를 고르고</li>
							<li>보낼 내용을 확인하고</li>
							<li>카톡에서 링크로 보내요.</li>
						</ol>
						<p>
							돈·메모·사진·숙소 주소는 기본 제외예요. 링크를 가진 사람은 내용을
							볼 수 있어요. 실시간 공동 편집은 아니며 바뀐 일정은 새 링크로
							보내요.
						</p>
						<button
							type="button"
							className="button primary"
							onClick={async () => {
								try {
									const sampleTrip = { ...t, expenses: [] };
									sampleTrip.days[0]!.items = [
										{
											id: "sample-park",
											title: "오호리공원",
											type: "spot",
											area: "후쿠오카",
											time: "10:00",
											estimateJpy: null,
											mapQuery: null,
											note: null,
											completed: false,
										},
									];
									setSample(
										await channelShareLink(
											await encodeShare(selectShare(sampleTrip)),
										),
									);
								} catch {
									setError(
										"공유 예시를 열지 못했어요. 내 여행을 만든 뒤 글로 보내기를 이용해 주세요.",
									);
								}
							}}
						>
							샘플 일정 공유 체험
						</button>
						{sample && (
							<a className="button secondary" href={sample}>
								받은 일정 열기
							</a>
						)}
					</>
				)}
				{slug === "trip-split" && (
					<>
						<p>
							여행을 만들기 전에 공동 지출 하나를 나눠 보세요. 엔화 1엔까지
							나누며 남은 엔은 낸 사람 몫에 붙여요.
						</p>
						<label>
							인원
							<input
								type="number"
								min={1}
								max={12}
								value={people}
								onChange={(e) => setPeople(Number(e.target.value))}
							/>
						</label>
						<label>
							내가 낸 총액 · 엔
							<input
								type="number"
								min={1}
								value={amount}
								onChange={(e) => setAmount(Number(e.target.value))}
							/>
						</label>
						<div role="status">
							{settleExpenses(t).rows.map((r) => (
								<p key={r.from}>
									일행{r.from.replace("party-", "")} → 나 · ¥
									{r.amountJpy.toLocaleString()}
								</p>
							))}
						</div>
						<p>
							실제 여행에서는 낸 사람·나눌 사람을 지출마다 골라 정산할 수
							있어요. 송금은 직접 해 주세요.
						</p>
					</>
				)}
				{slug === "packing" && (
					<>
						<p>
							달·일수·동행 유형으로 필요한 준비물을 골라요. 여권·입국·항공편은
							공식 안내로 다시 확인해 주세요.
						</p>
						<label>
							출발 월
							<input
								type="number"
								min={1}
								max={12}
								value={month}
								onChange={(e) => setMonth(Number(e.target.value))}
							/>
						</label>
						<label>
							여행 일수
							<input
								type="number"
								min={1}
								max={31}
								value={days}
								onChange={(e) => setDays(Number(e.target.value))}
							/>
						</label>
						<label>
							동행
							<select
								value={companion}
								onChange={(e) => setCompanion(e.target.value)}
							>
								<option value="other">공통</option>
								<option value="baby">아기 동반</option>
								<option value="parents">부모님 동반</option>
							</select>
						</label>
						<ul>
							{packingSuggestions(month, days, companion).map((p) => (
								<li key={p.id}>
									<label>
										<input
											type="checkbox"
											checked={packingSelected.includes(p.id)}
											onChange={(e) =>
												setPackingSelected(
													e.target.checked
														? [...packingSelected, p.id]
														: packingSelected.filter((id) => id !== p.id),
												)
											}
										/>
										{p.title}
									</label>
								</li>
							))}
						</ul>
						<div className="print-checklist">
							<h2>일본여행 준비물 · {month}월</h2>
							{packingSuggestions(month, days, companion).map((p) => (
								<p key={p.id}>
									{packingSelected.includes(p.id) ? "☑" : "□"} {p.title}
								</p>
							))}
						</div>
						<button
							type="button"
							className="button secondary"
							onClick={() => window.print()}
						>
							준비물 인쇄·PDF
						</button>
						<a
							href={`/trips/new?packingMonth=${month}&companion=${companion}&days=${days}&packing=${packingSelected.join(",")}`}
						>
							내 여행에 준비물 담기
						</a>
					</>
				)}
				{slug === "move-trip" && (
					<>
						<p>
							다른 앱이나 구글 시트의 일정 글을 붙여 넣으면 날짜·시간·장소를
							나눠요. 원래 앱과 계정에는 접근하지 않아요.
						</p>
						<p>트리플은 2026년 11월 3일 종료되며 일행·가계부·체크리스트는 NOL로 이관되지 않아요. <a href="https://triple.guide/articles/213be3ee-d110-4542-9c92-8a6d1e7a0349">트리플 공지 확인</a></p><p>일정은 글로 붙이고, 가계부는 여행을 만든 뒤 돈 화면에서 CSV 열을 맞춰 옮겨요. 일행과 체크리스트는 새로 적어 주세요.</p><label>여행 가는 날<input type="date" value={moveStart} onChange={e => setMoveStart(e.target.value)} /></label>
						<label>
							일정 글
							<textarea
								rows={8}
                                placeholder={"1일차\n10:00 오호리공원\n2일차\n11:00 하카타역"}
								maxLength={50000}
								value={text}
								onChange={(e) => setText(e.target.value)}
							/>
						</label>
						<h2>미리보기</h2>
                        {!moveStart && <p>가는 날을 고르면 날짜별 미리보기가 열려요.</p>}
                        {moveStart && !rows.length && <p>읽을 일정이 없어요. 예: 1일차, 10:00 오호리공원</p>}
						<ol>
							{rows.map((r, i) => (
								<li key={`${r.day}:${r.time}:${r.title}`}>
									{r.day === null ? "날짜 없는 후보" : `${r.day + 1}일차`} ·{" "}
									{r.time} {r.title}
								</li>
							))}
						</ol>
						<p>
							내 여행 만들기에서 붙여 넣어 만들기를 열고 이 글을 넣으면, 장소와
							날짜를 고친 뒤 저장할 수 있어요.
						</p>
						<a
							className="button primary"
							href={`/trips/new?paste=1&pasteDraft=1&startDate=${encodeURIComponent(moveStart)}`}
							onClick={(event) => {
                                if (!moveStart || !rows.length) { event.preventDefault(); return; }
								sessionStorage.setItem("jgm-paste-text", text);
								sessionStorage.setItem("jgm-paste-draft", JSON.stringify(rows));
							}}
						>
							붙여 넣어 여행 만들기
						</a>
						<p>
							트리플의 일정은 글로 옮겨 사용해요. 전용 백업 파일을 직접
							해석하거나 연동하지 않아요. 트리플은 해당 회사의 상표예요.
						</p>
					</>
				)}
				<p role="alert">{error}</p>
				<a className="button secondary" href="/trips/new">
					내 여행 만들기
				</a>
				<nav>
					{Object.entries(guideTitles).map(([key, title]) => (
						<p key={key}>
							<a href={`/guides/${key}`}>{title}</a>
						</p>
					))}
				</nav>
			</article>
		</Layout>
	);
}
